import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, Surface, Badge, Button } from "@/components/ui-kit";
import TyreMap from "@/components/TyreMap";
import ComplianceDocsPanel from "@/components/ComplianceDocsPanel";
import { ArrowLeft, UploadSimple, FileText, Trash, DownloadSimple, Printer } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { downloadCsv, printElement } from "@/lib/csv";

export default function VehicleDetail() {
    const { id } = useParams();
    const [v, setV] = useState(null);
    const [tyres, setTyres] = useState([]);
    const [docs, setDocs] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [batteryLedger, setBatteryLedger] = useState([]);
    const [tab, setTab] = useState("overview");
    const tyreMapRef = useRef(null);

    const exportTyresCsv = () => {
        if (!v) return;
        downloadCsv(`${v.reg_number}-tyres.csv`, tyres, [
            ["POSITION", "position"],
            ["SERIAL", "serial_number"],
            ["BRAND", "brand"],
            ["MODEL", "model"],
            ["SIZE", "size"],
            ["RETREAD COUNT", "retread_count"],
            ["STATUS", "status"],
            ["ODO AT FIT", "odometer_at_fit"],
            ["LIVE KM", (t) => t.live_km ?? t.total_km ?? 0],
            ["KM ON THIS VEHICLE", (t) => t.current_km_run ?? 0],
        ]);
    };

    const exportLedgerCsv = () => {
        if (!v) return;
        downloadCsv(`${v.reg_number}-battery-ledger.csv`, batteryLedger, [
            ["BATTERY SERIAL", (r) => r.battery?.serial_number || r.battery_id],
            ["BRAND", (r) => r.battery?.brand || ""],
            ["MODEL", (r) => r.battery?.model || ""],
            ["VOLTAGE", (r) => r.battery?.voltage || ""],
            ["CAPACITY (Ah)", (r) => r.battery?.capacity_ah || ""],
            ["FITTED ON", (r) => formatDate(r.assigned_at)],
            ["REMOVED ON", (r) => (r.removed_at ? formatDate(r.removed_at) : "")],
            ["ODO IN", "odometer_at_install"],
            ["ODO OUT", "odometer_at_remove"],
            ["KM RUN", "km_run"],
            ["STATUS", (r) => (r.is_open ? "FITTED" : "REMOVED")],
            ["REASON", "reason"],
        ]);
    };

    const exportWosCsv = () => {
        if (!v) return;
        downloadCsv(`${v.reg_number}-work-orders.csv`, workOrders, [
            ["WO #", "wo_number"],
            ["TITLE", "title"],
            ["TYPE", "type"],
            ["STATUS", "status"],
            ["PRIORITY", "priority"],
            ["CREATED", (w) => formatDate(w.created_at)],
            ["ODO", "odometer_reading"],
            ["TOTAL COST (INR)", (w) => w.total_cost || 0],
        ]);
    };

    const loadAll = useCallback(async () => {
        try {
            const [vehicle, vehicleTyres, vehicleDocs, allWO, ledger] = await Promise.all([
                api.get(`/vehicles/${id}`),
                api.get(`/tyres/by-vehicle/${id}`),
                api.get(`/vehicles/${id}/documents`),
                api.get("/work-orders"),
                api.get(`/vehicles/${id}/battery-ledger`),
            ]);
            setV(vehicle.data);
            setTyres(vehicleTyres.data);
            setDocs(vehicleDocs.data);
            setWorkOrders(allWO.data.filter((w) => w.vehicle_id === id));
            setBatteryLedger(ledger.data);
        } catch (e) { toast.error("Failed to load vehicle"); }
    }, [id]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const upload = async (file, label) => {
        const fd = new FormData(); fd.append("file", file);
        try {
            await api.post(`/upload?entity_type=vehicle&entity_id=${id}&label=${encodeURIComponent(label)}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Document uploaded");
            loadAll();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const delDoc = async (fid) => {
        if (!window.confirm("Delete document?")) return;
        await api.delete(`/files/${fid}`); loadAll();
    };

    if (!v) return <div className="p-8 label-mono">Loading…</div>;

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <Link to="/vehicles" className="inline-flex items-center gap-1 text-sm text-fleet-inkSoft hover:text-fleet-ink mb-4">
                <ArrowLeft size={14} /> Back to vehicles
            </Link>

            <PageHeader
                eyebrow={`VEHICLE · ${v.type?.toUpperCase()}`}
                title={v.reg_number}
                subtitle={`${v.make} ${v.model} ${v.year || ""} · ${v.fuel_type}`}
                action={<Badge variant={v.status}>{v.status?.replace("_", " ")}</Badge>}
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Surface className="p-4"><div className="label-mono">ODOMETER</div><div className="font-mono text-2xl mt-1">{(v.odometer || 0).toLocaleString()} <span className="text-sm text-fleet-inkMuted">km</span></div></Surface>
                <Surface className="p-4"><div className="label-mono">TYRES FITTED</div><div className="font-mono text-2xl mt-1">{tyres.length}</div></Surface>
                <Surface className="p-4"><div className="label-mono">WORK ORDERS</div><div className="font-mono text-2xl mt-1">{workOrders.length}</div></Surface>
                <Surface className="p-4"><div className="label-mono">DOCUMENTS</div><div className="font-mono text-2xl mt-1">{docs.length}</div></Surface>
            </div>

            <div className="flex gap-1 border-b border-fleet-border mb-6">
                {[
                    ["overview", "Overview"],
                    ["compliance", "Compliance"],
                    ["tyres", "Tyre Map"],
                    ["batteries", "Battery Ledger"],
                    ["wo", "Work Orders"],
                    ["docs", "Other Docs"],
                ].map(([k, l]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-fleet-ink text-fleet-ink font-medium" : "border-transparent text-fleet-inkSoft hover:text-fleet-ink"}`}
                        data-testid={`tab-${k}`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <Surface className="p-6">
                    <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                        {[
                            ["Registration", v.reg_number], ["Make", v.make], ["Model", v.model],
                            ["Year", v.year || "—"], ["Category", v.type], ["Fuel", v.fuel_type],
                            ["GVW (kgs)", v.gvw_kg ? Number(v.gvw_kg).toLocaleString() : "—"],
                            ["Vehicle type", v.vehicle_type_code || "—"],
                            ["Status", v.status],
                            ["Created", formatDate(v.created_at)],
                        ].map(([k, val]) => (
                            <div key={k}>
                                <dt className="label-mono">{k}</dt>
                                <dd className="font-mono mt-0.5">{val}</dd>
                            </div>
                        ))}
                    </dl>
                </Surface>
            )}

            {tab === "compliance" && <ComplianceDocsPanel vehicleId={id} />}

            {tab === "tyres" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="font-display text-lg font-semibold">Tyre Position Map</h3>
                        <div className="flex gap-2">
                            <Button variant="secondary" size="sm" onClick={exportTyresCsv} data-testid="tyres-export-csv">
                                <DownloadSimple size={14} /> CSV
                            </Button>
                            <Button variant="secondary" size="sm"
                                onClick={() => printElement(tyreMapRef.current, `${v.reg_number} · Tyre Map`)}
                                data-testid="tyres-export-pdf">
                                <Printer size={14} /> Print / PDF
                            </Button>
                        </div>
                    </div>
                    <div ref={tyreMapRef}>
                        <TyreMap layoutCode={v.vehicle_type_code || "truck_6"} tyres={tyres} />
                    </div>
                </Surface>
            )}

            {tab === "batteries" && (
                <Surface className="overflow-hidden">
                    <div className="px-5 py-4 border-b border-fleet-border flex items-center justify-between">
                        <div>
                            <h3 className="font-display text-lg font-semibold">Battery Ledger</h3>
                            <p className="text-xs text-fleet-inkSoft mt-0.5">Every battery ever fitted on this vehicle, with km contributed while on it.</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <div className="label-mono">TOTAL KM ON BATTERIES</div>
                                <div className="font-mono text-xl mt-0.5">
                                    {batteryLedger.reduce((sum, r) => sum + (r.km_run || 0), 0).toLocaleString()} km
                                </div>
                            </div>
                            <Button variant="secondary" size="sm" onClick={exportLedgerCsv} data-testid="ledger-export-csv">
                                <DownloadSimple size={14} /> CSV
                            </Button>
                        </div>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                {["BATTERY", "BRAND/MODEL", "FITTED", "REMOVED", "ODO IN", "ODO OUT", "KM RUN", "STATUS", "REASON"].map((h) => (
                                    <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {batteryLedger.map((r) => (
                                <tr key={r.id} className="border-t border-fleet-border data-grid-row" data-testid={`battery-ledger-row-${r.id}`}>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        <Link to="/batteries" className="hover:underline">{r.battery?.serial_number || r.battery_id?.slice(0, 8)}</Link>
                                    </td>
                                    <td className="px-5 py-3">
                                        {r.battery?.brand || "—"} {r.battery?.model || ""}
                                        {r.battery?.voltage ? <span className="text-xs text-fleet-inkSoft ml-1">· {r.battery.voltage}V {r.battery.capacity_ah}Ah</span> : null}
                                    </td>
                                    <td className="px-5 py-3 font-mono">{formatDate(r.assigned_at)}</td>
                                    <td className="px-5 py-3 font-mono">{r.removed_at ? formatDate(r.removed_at) : <span className="text-fleet-inkMuted">—</span>}</td>
                                    <td className="px-5 py-3 font-mono">{(r.odometer_at_install || 0).toLocaleString()}</td>
                                    <td className="px-5 py-3 font-mono">{r.odometer_at_remove != null ? r.odometer_at_remove.toLocaleString() : <span className="text-fleet-inkMuted">—</span>}</td>
                                    <td className="px-5 py-3 font-mono font-semibold">{(r.km_run || 0).toLocaleString()}</td>
                                    <td className="px-5 py-3">
                                        <Badge variant={r.is_open ? "healthy" : "preventive"}>{r.is_open ? "FITTED" : "REMOVED"}</Badge>
                                    </td>
                                    <td className="px-5 py-3 text-xs text-fleet-inkSoft">{r.reason || "—"}</td>
                                </tr>
                            ))}
                            {batteryLedger.length === 0 && (
                                <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">No batteries have been fitted on this vehicle yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "wo" && (
                <Surface className="overflow-hidden">
                    <div className="px-5 py-4 border-b border-fleet-border flex items-center justify-between">
                        <h3 className="font-display text-lg font-semibold">Work Orders</h3>
                        <Button variant="secondary" size="sm" onClick={exportWosCsv} data-testid="wo-export-csv">
                            <DownloadSimple size={14} /> CSV
                        </Button>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                {["WO #", "TITLE", "TYPE", "STATUS", "COST"].map((h) => (
                                    <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {workOrders.map((w) => (
                                <tr key={w.id} className="border-t border-fleet-border data-grid-row">
                                    <td className="px-5 py-3 font-mono">{w.wo_number}</td>
                                    <td className="px-5 py-3">{w.title}</td>
                                    <td className="px-5 py-3"><Badge variant={w.type}>{w.type}</Badge></td>
                                    <td className="px-5 py-3"><Badge variant={w.status}>{w.status?.replace("_", " ")}</Badge></td>
                                    <td className="px-5 py-3 font-mono">₹{(w.total_cost || 0).toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            {workOrders.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">No work orders.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "docs" && (
                <Surface className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Documents</h3>
                        <label className="cursor-pointer">
                            <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        const label = window.prompt("Document type (RC, Insurance, Permit…)", "Document") || "Document";
                                        upload(file, label);
                                    }
                                    e.target.value = "";
                                }}
                                data-testid="upload-doc-input"
                            />
                            <Button as="span" variant="primary"><UploadSimple size={16} /> Upload</Button>
                        </label>
                    </div>

                    {docs.length === 0 ? (
                        <div className="text-center py-10 text-sm text-fleet-inkSoft">No documents uploaded yet.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {docs.map((d) => (
                                <div key={d.id} className="border border-fleet-border rounded-md p-4 flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-md bg-slate-50 border border-fleet-border flex items-center justify-center text-fleet-inkSoft">
                                        <FileText size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                        <div className="text-xs text-fleet-inkSoft">{d.label || "Document"} · {Math.round((d.size || 0) / 1024)} KB</div>
                                        <div className="mt-2 flex gap-2">
                                            <a href={fileUrl(d.id)} target="_blank" rel="noreferrer" className="text-xs text-fleet-accent hover:underline">View</a>
                                            <button onClick={() => delDoc(d.id)} className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"><Trash size={12} /> Remove</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Surface>
            )}
        </div>
    );
}
